/**
 * Chart documentation shown behind the info icon on the Company Masterdashboard.
 *
 * Keys match the chart link property names in `CompanyMasterdashboardComponent`.
 *
 * Most charts use a fixed loader window. Running ARPU uses all available cohort
 * days.
 */
export const CompanyMasterdashboardChartDocs = {
    // USERS

    dau: /* HTML */ `<p>Players active on each day.</p>
        <p><strong>Axes:</strong> X - day; Y - active players.</p>
        <p>
            <strong>What's counted:</strong> the daily active user count from the
            MOPA company KPI table. Fixed window: the last 30 days.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    mau: /* HTML */ `<p>Players active in each month.</p>
        <p><strong>Axes:</strong> X - month; Y - active players.</p>
        <p>
            <strong>What's counted:</strong> the monthly active user count,
            written once a month per project, store, country and payer bucket -
            a player active on many days counts once, and this is not a sum of
            daily DAU. Window: current month and 15 previous months.
        </p>
        <p>
            Buckets that are not split out are added together, so a player who
            moved between them mid-month - above all became a payer - counts in
            each.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    monthly_avg_dau: /* HTML */ `<p>
            Average daily active users across each month.
        </p>
        <pre>
  DAU summed over the month (numerator)
  ------------------------------
  days in that month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - average daily active users.
        </p>
        <p>
            The current month divides by the days elapsed so far, not by the full
            calendar month. Window: current month and 15 previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    new_users_daily: /* HTML */ `<p>
            Players whose first session falls on each day.
        </p>
        <p><strong>Axes:</strong> X - day; Y - new players.</p>
        <p>
            <strong>What's counted:</strong> new users from the company KPI
            table - BI's own first-session count, not store or AppsFlyer
            installs. Fixed window: the last 30 days.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    new_users_monthly: /* HTML */ `<p>
            Players whose first session falls in each month.
        </p>
        <p><strong>Axes:</strong> X - month; Y - new players.</p>
        <p>
            <strong>What's counted:</strong> new users summed over the month -
            BI's own first-session count, not store or AppsFlyer installs. Window:
            current month and 15 previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    stickiness_dau_mau: /* HTML */ `<p>
            How much of the monthly base shows up on an average day.
        </p>
        <pre>
  DAU summed over the month (numerator)
  ------------------------------
  30-day active users summed over the month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - the ratio itself, not a
            percentage - 0.2 means the average day brings a fifth of the
            monthly base.
        </p>
        <p>
            DAU includes valid players only, while the 30-day denominator does
            not. Window: current month and 15 previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    avg_user_lifetime_d7: /* HTML */ `<p>
            Estimated days a cohort stays active, measured at day 7.
        </p>
        <p>
            <strong>Axes:</strong> X - the cohort's first-open date; Y -
            expected active days.
        </p>
        <p>
            <strong>What's counted:</strong> the per-cohort D7 lifetime estimate,
            averaged across cohorts and weighted by cohort size. Plotted against
            the cohort's first-open date. Fixed window: cohorts that first opened
            between 37 and 7 days ago, so every one of them has reached day 7.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    avg_user_lifetime_d30: /* HTML */ `<p>
            Estimated days a cohort stays active, measured at day 30.
        </p>
        <p>
            <strong>Axes:</strong> X - the cohort's first-open date; Y -
            expected active days.
        </p>
        <p>
            <strong>What's counted:</strong> the per-cohort D30 lifetime
            estimate, averaged across cohorts and weighted by cohort size.
            Plotted against the cohort's first-open date. Fixed window: cohorts
            that first opened between 59 and 30 days ago, so every one of them
            has reached day 30.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    avg_user_lifetime_d30_monthly: /* HTML */ `<p>
            Estimated days a cohort stays active, measured at day 30, by month of
            first session.
        </p>
        <p>
            <strong>Axes:</strong> X - the cohort's first-open month; Y -
            expected active days.
        </p>
        <p>
            <strong>What's counted:</strong> as the daily D30 chart, with cohorts
            grouped by their install month. Window: current month and 15 previous
            months; cohorts younger than 30 days are left out.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    // REVENUE

    daily_income: /* HTML */ `<p>Revenue recorded on each day.</p>
        <p>
            <strong>Axes:</strong> X - day; Y - revenue in the selected
            currency.
        </p>
        <p>
            <strong>What's counted:</strong> daily revenue summed over matching
            MOPA rows; net/gross and the currency follow their toggles. The
            Total, In-app and Ad checkboxes independently select one or more
            series. Fixed window: the last 30 days.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    monthly_income: /* HTML */ `<p>Revenue recorded in each month.</p>
        <p>
            <strong>Axes:</strong> X - month; Y - revenue in the selected
            currency.
        </p>
        <p>
            <strong>What's counted:</strong> daily revenue summed over the month;
            net/gross and currency follow their toggles. The Total, In-app and
            Ad checkboxes independently select one or more series. Window:
            current month and 15 previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    arpdau: /* HTML */ `<p>Revenue per active user on each day.</p>
        <pre>
  revenue that day (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - day; Y - revenue per active user, in the
            selected currency.
        </p>
        <p>Fixed window: the last 30 days.</p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    monthly_avg_arpdau: /* HTML */ `<p>
            Revenue per active user across each month.
        </p>
        <pre>
  revenue summed over the month (numerator)
  ------------------------------
  DAU summed over the month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - revenue per active user, in
            the selected currency.
        </p>
        <p>
            Weighted: sum both sides first, then divide, so the denominator
            counts player-days, not distinct monthly players. Window: current
            month and 15 previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    monthly_arpu_d30: /* HTML */ `<p>
            Revenue per user that a monthly cohort produced by day 30.
        </p>
        <pre>
  cohort revenue up to day 30 (numerator)
  ------------------------------
  initial size of that cohort (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - the cohort's first-open month; Y -
            revenue per user by day 30, in the selected currency.
        </p>
        <p>
            Only cohorts that have reached day 30 are counted, so the newest
            month stays empty until it matures. Window: current month and 15
            previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    running_arpu: /* HTML */ `<p>
            Cumulative revenue per user of a cohort, by day number since first
            session.
        </p>
        <pre>
  cohort revenue at that day number (numerator)
  ------------------------------
  cohort size (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - days since first session, day 0 being the
            first-session day; Y - cumulative revenue per user, in the selected
            currency.
        </p>
        <p>
            The line is the running sum of those daily values, so it only climbs.
            <strong>The two sides cover different populations:</strong> revenue
            counts valid players only, while the cohort size counts every
            <code>player_biviz_dims</code> record with no validation filter, so
            the denominator is the larger of the two and the curve sits low.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_revenue_by_day_no</code>.
        </p>`,

    conversion_rate: /* HTML */ `<p>
            Share of active players who paid on each day.
        </p>
        <pre>
  paying players that day (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - day; Y - share of active players who
            paid, in %.
        </p>
        <p>Fixed window: the last 30 days.</p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    monthly_conversion_rate: /* HTML */ `<p>
            Share of active players who paid, across each month.
        </p>
        <pre>
  paying players summed over the month (numerator)
  ------------------------------
  DAU summed over the month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - share of active players who
            paid, in %.
        </p>
        <p>
            Both sides are daily sums, so a player who is active and pays on
            several days counts several times. Window: current month and 15
            previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    // USER ACQUISITION

    daily_ads_cost: /* HTML */ `<p>
            Money spent on acquisition campaigns each day.
        </p>
        <p>
            <strong>Axes:</strong> X - day; Y - spend in the selected currency.
        </p>
        <p>
            <strong>What's counted:</strong> MOPA campaign cost summed in the
            selected currency. Fixed window: the last 30 days.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>.
        </p>`,

    monthly_ads_cost: /* HTML */ `<p>
            Money spent on acquisition campaigns each month.
        </p>
        <p>
            <strong>Axes:</strong> X - month; Y - spend in the selected
            currency.
        </p>
        <p>
            <strong>What's counted:</strong> campaign cost summed over the month,
            in the selected currency. Window: current month and 15 previous
            months.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>.
        </p>`,

    daily_cpi: /* HTML */ `<p>Cost per new user on each day.</p>
        <pre>
  campaign cost that day (numerator)
  ------------------------------
  new users that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - day; Y - cost per new user, in the
            selected currency.
        </p>
        <p>
            The denominator is BI's own new users, <strong>not</strong> attributed
            installs, so this differs from the CPI on the Campaigns dashboard:
            organic players are in the denominator but bring no cost. Fixed
            window: the last 30 days.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>
            (cost), <code>bi_viz.dashboard_company_player_kpis_daily</code> (new
            users).
        </p>`,

    monthly_cpi: /* HTML */ `<p>Cost per new user in each month.</p>
        <pre>
  campaign cost that month (numerator)
  ------------------------------
  new users that month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - cost per new user, in the
            selected currency.
        </p>
        <p>
            As the daily chart, the denominator is BI's new users rather than
            attributed installs, so organic players dilute it. Window: current
            month and 15 previous months.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>
            (cost), <code>bi_viz.dashboard_company_player_kpis_daily</code> (new
            users).
        </p>`,

    first_margin_daily: /* HTML */ `<p>
            Revenue minus campaign cost on each date.
        </p>
        <p>
            <strong>Axes:</strong> X - day; Y - revenue minus cost in the
            selected currency, so below zero the day cost more than it earned.
        </p>
        <p>
            Revenue includes valid player-days with positive net revenue in PLN or
            USD. Net/gross and currency follow their toggles. Fixed window: the
            last 30 days.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>.
        </p>`,

    first_margin_monthly: /* HTML */ `<p>
            Revenue minus campaign cost in each month.
        </p>
        <p>
            <strong>Axes:</strong> X - month; Y - revenue minus cost in the
            selected currency, so below zero the month cost more than it
            earned.
        </p>
        <p>
            Revenue includes valid player-days with positive net revenue in PLN or
            USD. Net/gross and currency follow their toggles. Window: current
            month and 15 previous months.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>.
        </p>`,

    roas_d7_daily: /* HTML */ `<p>
            Mature D7 cohort revenue divided by AppsFlyer cost reported on the
            same calendar date.
        </p>
        <pre>
  cohort revenue up to day 7 (numerator)
  ------------------------------
  AppsFlyer cost reported that date (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - calendar date; Y - day-7 revenue against
            spend, in % - 100% is break-even.
        </p>
        <p>
            Revenue uses first-open cohorts; cost uses reporting dates. The sources
            are joined by date and filters, not matched campaign cohorts. Window:
            first-open 37-8 days ago.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>
            (cost), <code>bi_viz.dashboard_company_player_kpis_daily</code>
            (revenue).
        </p>`,

    roas_d7_monthly: /* HTML */ `<p>
            Mature D7 cohort revenue divided by AppsFlyer cost, by month.
        </p>
        <pre>
  cohort revenue up to day 7 (numerator)
  ------------------------------
  AppsFlyer cost reported that month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - day-7 revenue against spend,
            in % - 100% is break-even.
        </p>
        <p>
            Revenue uses first-open cohorts; cost uses reporting dates. They are
            joined by month and filters, not matched campaign cohorts. Window:
            current month and 15 previous months; cohorts newer than D7 are out.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>
            (cost), <code>bi_viz.dashboard_company_player_kpis_daily</code>
            (revenue).
        </p>`,

    roas_d30_daily: /* HTML */ `<p>
            Mature D30 cohort revenue divided by AppsFlyer cost reported on the
            same calendar date.
        </p>
        <pre>
  cohort revenue up to day 30 (numerator)
  ------------------------------
  AppsFlyer cost reported that date (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - calendar date; Y - day-30 revenue against
            spend, in % - 100% is break-even.
        </p>
        <p>
            Revenue uses first-open cohorts; cost uses reporting dates. The sources
            are joined by date and filters, not matched campaign cohorts. Window:
            first-open 59-31 days ago.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>
            (cost), <code>bi_viz.dashboard_company_player_kpis_daily</code>
            (revenue).
        </p>`,

    roas_d30_monthly: /* HTML */ `<p>
            Mature D30 cohort revenue divided by AppsFlyer cost, by month.
        </p>
        <pre>
  cohort revenue up to day 30 (numerator)
  ------------------------------
  AppsFlyer cost reported that month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - day-30 revenue against spend,
            in % - 100% is break-even.
        </p>
        <p>
            Revenue uses first-open cohorts; cost uses reporting dates. They are
            joined by month and filters, not matched campaign cohorts. Window:
            current month and 15 previous months; cohorts newer than D30 are out.
        </p>
        <p>
            Source: <code>bi_viz.dashboard_company_campaign_kpis_daily</code>
            (cost), <code>bi_viz.dashboard_company_player_kpis_daily</code>
            (revenue).
        </p>`,

    // ADS

    ads_engagement_rate: /* HTML */ `<p>
            Share of active players who received an ad reward each day.
        </p>
        <pre>
  users with ad_rewards > 0 that day (numerator)
  ------------------------------
  DAU that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - day; Y - share of active players who got
            an ad reward, in %.
        </p>
        <p>Fixed window: the last 30 days.</p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    monthly_engagement_rate: /* HTML */ `<p>
            Share of active players who received an ad reward, across each month.
        </p>
        <pre>
  users with ad_rewards > 0 summed over the month (numerator)
  ------------------------------
  DAU summed over the month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - share of active players who
            got an ad reward, in %.
        </p>
        <p>
            Both sides are daily sums, so this is a weighted average of the daily
            rates, not a rate over distinct monthly players. Window: current
            month and 15 previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    ads_impressions: /* HTML */ `<p>Ad show starts each day.</p>
        <p><strong>Axes:</strong> X - day; Y - ad show starts.</p>
        <p>
            <strong>What's counted:</strong> <code>ad_show_starts</code>, not
            acquisition-campaign impressions. Fixed window: the last 30 days.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    ads_impressions_monthly: /* HTML */ `<p>
            Ad show starts each month.
        </p>
        <p><strong>Axes:</strong> X - month; Y - ad show starts.</p>
        <p>
            <strong>What's counted:</strong> <code>ad_show_starts</code>, not
            acquisition-campaign impressions. Window: current month and 15
            previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    impressions_per_deu: /* HTML */ `<p>
            Ad show starts per rewarded user each day.
        </p>
        <pre>
  ad_show_starts that day (numerator)
  ------------------------------
  users with ad_rewards > 0 that day (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - day; Y - ad show starts per rewarded
            user.
        </p>
        <p>Fixed window: the last 30 days.</p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,

    impressions_per_deu_monthly: /* HTML */ `<p>
            Ad show starts per rewarded user, across each month.
        </p>
        <pre>
  ad_show_starts summed over the month (numerator)
  ------------------------------
  users with ad_rewards > 0 summed over the month (denominator)
    </pre
        >
        <p>
            <strong>Axes:</strong> X - month; Y - ad show starts per rewarded
            user.
        </p>
        <p>
            Both sides are daily sums, so a rewarded player on several days counts
            several times in the denominator. Window: current month and 15
            previous months.
        </p>
        <p>Source: <code>bi_viz.dashboard_company_player_kpis_daily</code>.</p>`,
} as const;
